import "./AddVariation.scss";
import { useEffect, useState } from "react";
import { AdminService } from "../utils";
import { useAsync } from "../../../utils/hooks/useAsync";
import { toast, ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import UploadImage from "../UploadImage";
import Label from "../../Label";
import useDevice from "../../../utils/hooks/useDevice";
import { getGaugeSelectOptionLabel, getGaugeSelectOptions } from "./util";
import { displayMoney } from "../../../utils/displayMoney";
import { normalizeWastage } from "../../../utils/wastage";

type Props = {
  product: Product;
  manufacturingCost: ManufacturingCost[];
  setState: React.Dispatch<React.SetStateAction<any>>;
  existingVariations?: Variation[];
};

type WastageOption = Wastage | "";
type VariationForm = Omit<Variation, "wastage"> & { wastage: WastageOption };
type GroupedVariationsForm = { gauge: string; variations: VariationForm[] }[];

const AddVariation: React.FC<Props> = ({
  product,
  setState,
  manufacturingCost,
  existingVariations,
}) => {
  const navigate = useNavigate();
  const route = useLocation();
  const { run } = useAsync();
  const { isMobile } = useDevice();
  const [hilos, setHilos] = useState<string[]>([]);
  const [isSolid, setIsSolid] = useState<boolean>(false);
  const [longitudes, setLongitudes] = useState<number[]>([]);
  const [checkedVariation, setCheckedVariation] = useState<boolean>(false);
  const defaultWastage = (material: string): WastageOption => {
    if (material === "silver") {
      return "NO_WASTAGE";
    }
    return "";
  };

  const wastageLabel = (value?: WastageOption) => {
    if (value === "NO_WASTAGE") {
      return "Sin merma";
    }
    return value || "";
  };

  const [variation, setVariation] = useState<VariationForm>({
    weight: "",
    length: "",
    color: product.material == "gold" ? "" : "plata",
    wastage: defaultWastage(product.material),
    gauge: "",
    productId: product._id || "",
    manufacturingCostId: "",
  });
  const [variations, setVariations] = useState<VariationForm[]>([]);

  const [groupedVariations, setGroupedVariations] =
    useState<GroupedVariationsForm>([]);
  const collapsible = (gauge: string) => (
    <div className="trigger">
      <p>{gauge}</p>
    </div>
  );

  useEffect(() => {
    let arrayLongitud = [
      18, 21, 24, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100,
    ];

    setLongitudes(arrayLongitud);
    setHilos(getGaugeSelectOptions());

    (product.department == "chain" || "brazalete") &&
      !product.category.includes("pendant") &&
      !product.category.includes("rock") &&
      !product.category.includes("hollow") &&
      !product.category.includes("hoop") &&
      !product.category.includes("finding") &&
      !product.isCustom &&
      setIsSolid(true);
  }, [, existingVariations]);

  useEffect(() => {
    validVariation();
  }, [variation]);

  useEffect(() => {
    let auxGroupedVariations: GroupedVariationsForm = [];
    variations.map((variation) => {
      let index = auxGroupedVariations.findIndex(
        (el) => el.gauge == variation.gauge
      );
      if (index >= 0) {
        auxGroupedVariations[index].variations.push(variation);
      } else {
          let aux: VariationForm[] = [];
        aux.push(variation);
        auxGroupedVariations.push({
          gauge: variation.gauge,
          variations: aux,
        });
      }
    });

    setGroupedVariations(
      auxGroupedVariations.sort(
        (el, el2) => Number(el.gauge) - Number(el2.gauge)
      )
    );
  }, [variations]);

  const handleChange = (e: any) => {
    const nextValue =
      e.currentTarget.name === "wastage"
        ? (e.currentTarget.value as WastageOption)
        : e.currentTarget.value;
    setVariation({
      ...variation,
      [e.currentTarget.name]:
        e.currentTarget.name == "gauge" &&
        (product.department == "chain" || product.department == "brazalete") &&
        isSolid
          ? nextValue
          : e.currentTarget.name == "weight"
          ? String(nextValue).replace(",", ".")
          : nextValue,
    });
  };
  
  const submitForm = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    checkedVariation && setVariations([...variations, variation]);
    resetForm();
  };

  const resetForm = () => {
    setVariation({
      weight: "",
      length: "",
      color: product.material == "silver" ? "plata" : "",
      gauge: "",
      productId: variation.productId,
      manufacturingCostId: "",
      wastage: defaultWastage(product.material),
    });
  };

  const validVariation = () => {
    variation.color != "" &&
    variation.gauge != "" &&
    variation.length != "" &&
    variation.manufacturingCostId != "" &&
    variation.productId != "" &&
    variation.weight != "" &&
    variation.wastage != "" &&
    !variations.some(
      (el) =>
        el.color == variation.color &&
        el.gauge == variation.gauge &&
        el.length == variation.length
    )
      ? existingVariations
        ? !existingVariations.some(
            (el) =>
              el.color == variation.color &&
              el.gauge == variation.gauge &&
              el.length == variation.length
          )
          ? setCheckedVariation(true)
          : setCheckedVariation(false)
        : setCheckedVariation(true)
      : setCheckedVariation(false);
  };

  const removeVariation = (variationref: VariationForm) => {
    toast.done("Variacion descartada");
    setVariations(
      variations.filter(
        (el) =>
          el.color != variationref.color ||
          el.gauge != variationref.gauge ||
          el.length != variationref.length ||
          el.manufacturingCostId != variationref.manufacturingCostId ||
          el.weight != variationref.weight ||
          el.wastage != variationref.wastage
      )
    );
  };

  const saveVariations = async () => {
    const normalizedVariations: Variation[] = variations.map((vari) => ({
      ...vari,
      wastage: normalizeWastage(vari.wastage, product.material),
    }));
    run(AdminService.saveVariant(normalizedVariations)).then((r) => {
      toast.success("Variaciones guardadas correctamente");
      setTimeout(() => {
        setState(true);
        setVariations([]);
        navigate("/admin/see");
      }, 2000);
    });
  };

  const manufacturingCostVariation = (vari: VariationForm) =>
    manufacturingCost.find((el) => el._id == vari.manufacturingCostId)?.value;

  return (
    <div className="addVariationContainer">
      <ToastContainer autoClose={1500} position={"bottom-left"} />

      {!location.pathname.includes("admin/edit") && (
        <>
          <Label
            tag="h3"
            style={{
              textAlign: "left",
              fontFamily: "Mont-Semibold",
            }}
          >
            {product?.title}
          </Label>
          <UploadImage product={product} urlImage={product.thumbnailUrl} />
        </>
      )}
      <Label
        tag="h3"
        style={{
          textAlign: "left",
          fontFamily: "Mont-Semibold",
          fontSize: isMobile ? "16px" : "18px",
        }}
      >
        Variantes
      </Label>
      <form onSubmit={submitForm} className="add_variation_form_container">
        <div className="add_variation_form">
          {(product.department == "chain" ||
            product.department == "brazalete") &&
          isSolid ? (
            <>
              <label>
                Hilo:
                <select
                  name="gauge"
                  onChange={handleChange}
                  onClick={handleChange}
                  value={variation.gauge}
                >
                  <option value="" selected disabled hidden>
                    Seleccione
                  </option>
                  {hilos &&
                    hilos.map((hilo) => {
                      return (
                        <option key={hilo} value={hilo}>
                          {hilo}
                        </option>
                      );
                    })}
                </select>
              </label>
            </>
          ) : (
            <>
              <label>
                Espesor:
                <input
                  type={"text"}
                  name={"gauge"}
                  value={variation.gauge}
                  onChange={handleChange}
                />
              </label>
            </>
          )}
          {(product.department == "chain" ||
            product.department == "brazalete") &&
          isSolid ? (
            <>
              <label>
                Largo:
                <select
                  name="length"
                  onChange={handleChange}
                  onClick={handleChange}
                  value={variation.length}
                >
                  <option value="" selected disabled hidden>
                    Seleccione
                  </option>
                  {longitudes &&
                    longitudes.map((long) => {
                      return (
                        <option key={long} value={long}>
                          {long + " cm"}
                        </option>
                      );
                    })}
                </select>
              </label>
            </>
          ) : (
            <>
              <label>
                Medidas:
                <input
                  type={"text"}
                  name={"length"}
                  value={variation.length}
                  onChange={handleChange}
                />
              </label>
            </>
          )}
          <label>
            Peso:
            <input
              type={"text"}
              name={"weight"}
              value={variation.weight}
              onChange={handleChange}
            />
          </label>
          <label>
            Hechura:
            <select
              name="manufacturingCostId"
              onClick={handleChange}
              onChange={handleChange}
              value={variation.manufacturingCostId}
            >
              <option value="" selected disabled hidden>
                Seleccione
              </option>
              {manufacturingCost &&
                manufacturingCost.map((man) => {
                  if (man.material == product.material) {
                    return (
                      <option key={man._id} value={man._id}>
                        {man.description + " - " + displayMoney(man.value)}
                      </option>
                    );
                  }
                })}
            </select>{" "}
          </label>
          {product?.material === "gold" && (
            <>
              <label>
                Color:
                <select
                  name="color"
                  onChange={handleChange}
                  onClick={handleChange}
                  value={variation.color}
                >
                  <>
                    <option key={0} value="" selected disabled hidden>
                      Seleccione
                    </option>
                    <option key={1} value={"amarillo"}>
                      Amarillo
                    </option>
                    <option key={2} value={"rojo"}>
                      Rojo
                    </option>
                    <option key={3} value={"blanco"}>
                      Blanco
                    </option>
                  </>
                </select>
              </label>
            </>
          )}
          {product?.material === "silver" && (
            <>
              <label>
                Color:
                <select
                  name="color"
                  onChange={handleChange}
                  onClick={handleChange}
                  value={variation.color}
                >
                  <>
                    <option key={0} value="" selected disabled hidden>
                      Seleccione
                    </option>
                    <option key={1} value={"plata"}>
                      Plata
                    </option>
                    <option key={2} value={"soaked"}>
                      Empavonado
                    </option>
                    <option key={3} value={"rose"}>
                      Rosé
                    </option>
                    <option key={4} value={"golden"}>
                      Dorado
                    </option>
                  </>
                </select>
              </label>
            </>
          )}
          <>
            <label>
              Merma:
              <select
                name="wastage"
                onChange={handleChange}
                onClick={handleChange}
                value={variation.wastage}
              >
                <>
                  <option key={0} value="" selected disabled hidden>
                    Seleccione
                  </option>
                  {product.material === "gold" && (
                    <>
                      <option key={1} value={"810"}>
                        810
                      </option>
                      <option key={2} value={"825"}>
                        825
                      </option>
                    </>
                  )}
                  {product.material === "silver" && (
                    <option key={3} value={"NO_WASTAGE"}>
                      Sin merma
                    </option>
                  )}
                </>
              </select>
            </label>
          </>
        </div>
        {checkedVariation ? (
          <button type="submit">Agregar</button>
        ) : (
          <button type="submit" disabled={true}>
            Agregar
          </button>
        )}
      </form>
      {groupedVariations.length > 0 && (
        <div className="resultsAddVariationItems">
          {(product.department == "chain" ||
            product.department == "brazalete") &&
          isSolid ? (
            <Label
              tag="h3"
              style={{
                fontFamily: "Mont-Light",
                fontSize: "12px",
                textAlign: "start",
                width: "20%",
              }}
            >
              HILO
            </Label>
          ) : (
            <Label
              tag="h3"
              style={{
                fontFamily: "Mont-Light",
                fontSize: "12px",
                textAlign: "start",
                width: "20%",
              }}
            >
              ESPESOR
            </Label>
          )}
          {(product.department == "chain" ||
            product.department == "brazalete") &&
          isSolid ? (
            <Label
              tag="h3"
              style={{
                fontFamily: "Mont-Light",
                fontSize: "12px",
                textAlign: "start",
                width: "20%",
              }}
            >
              LARGO
            </Label>
          ) : (
            <Label
              tag="h3"
              style={{
                fontFamily: "Mont-Light",
                fontSize: "12px",
                textAlign: "start",
                width: "20%",
              }}
            >
              MEDIDAS
            </Label>
          )}
          <Label
            tag="h3"
            style={{
              fontFamily: "Mont-Light",
              fontSize: "12px",
              textAlign: "start",
              width: "20%",
            }}
          >
            PESO
          </Label>
          <Label
            tag="h3"
            style={{
              fontFamily: "Mont-Light",
              fontSize: "12px",
              textAlign: "start",
              width: "20%",
            }}
          >
            COLOR
          </Label>
          <Label
            tag="h3"
            style={{
              fontFamily: "Mont-Light",
              fontSize: "12px",
              textAlign: "start",
              width: "20%",
            }}
          >
            MERMA
          </Label>
          <Label
            tag="h3"
            style={{
              fontFamily: "Mont-Light",
              fontSize: "12px",
              textAlign: "start",
              width: "20%",
            }}
          >
            HECHURA
          </Label>

          <p
            style={{
              textAlign: "end",
              textDecoration: "underline",
              marginRight: 10,
              width: "20%",
            }}
          ></p>
        </div>
      )}
      {groupedVariations &&
        groupedVariations.map((gvar) => {
          return (
            <>
              {gvar.variations.map((vari) => {
                return (
                  <div
                    className="resultsAddVariationItems"
                    key={vari.productId + vari.gauge + vari.length + vari.color}
                  >
                    <p>{vari.gauge} </p>
                    <p>
                      {vari.length}{" "}
                      {(product.department == "chain" ||
                        product.department == "brazalete") &&
                      isSolid
                        ? "cm"
                        : "mm"}
                    </p>
                    <p>{vari.weight} g</p>
                    <p>{vari.color}</p>
                    <p>{wastageLabel(vari.wastage)}</p>
                    <p style={{ fontFamily: "Mont-bold" }}>
                      {manufacturingCostVariation(vari)
                        ? displayMoney(Number(manufacturingCostVariation(vari)))
                        : " "}
                    </p>

                    <p
                      onClick={() => removeVariation(vari)}
                      style={{
                        textAlign: "end",
                        textDecoration: "underline",
                        marginRight: 10,
                        fontSize: isMobile ? "13px" : "14px",
                        cursor: "pointer",
                      }}
                    >
                      Borrar
                    </p>
                  </div>
                );
              })}
            </>
          );
        })}
      {variations.length > 0 && (
        <div className="save_div">
          <button className="save" onClick={() => saveVariations()}>
            Guardar cambios
          </button>
        </div>
      )}
    </div>
  );
};

export default AddVariation;
